import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Cookie consent categories and interface
export interface CookieConsent {
  necessary: true;           // Always true, cannot be disabled
  analytics: boolean;        // Google Analytics, heatmaps
  marketing: boolean;        // Advertising, retargeting
  functional: boolean;       // Enhanced features
  consentDate: Date;
  version: string;          // Policy version
  ipAddress?: string;       // For audit trail
}

export type ConsentCategory = 'necessary' | 'analytics' | 'marketing' | 'functional';

export interface CookieConsentContextType {
  consent: CookieConsent | null;
  updateConsent: (newConsent: Partial<CookieConsent>) => void;
  hasConsent: (category: ConsentCategory) => boolean;
  isConsentGiven: () => boolean;
  showConsentBanner: boolean;
  showSettingsModal: boolean;
  acceptAllCookies: () => void;
  acceptEssentialOnly: () => void;
  resetConsent: () => void;
  getConsentSummary: () => string;
  openSettings: () => void;
  closeSettings: () => void;
}

// Default consent state (only necessary cookies)
const DEFAULT_CONSENT: CookieConsent = {
  necessary: true,
  analytics: false,
  marketing: false,
  functional: false,
  consentDate: new Date(),
  version: '1.0'
};

// Storage key for consent
const CONSENT_STORAGE_KEY = 'vst-cookie-consent';
const CURRENT_POLICY_VERSION = '1.0';

// Create context
const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

// Hook to use cookie consent context
export const useCookieConsent = (): CookieConsentContextType => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

// Provider props
interface CookieConsentProviderProps {
  children: ReactNode;
}

// Cookie Consent Provider
export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [showConsentBanner, setShowConsentBanner] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Load consent from localStorage on mount
  useEffect(() => {
    try {
      const savedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (savedConsent) {
        const parsedConsent: CookieConsent = JSON.parse(savedConsent);
        
        // Check if consent is still valid (same version)
        if (parsedConsent.version === CURRENT_POLICY_VERSION) {
          setConsent({
            ...parsedConsent,
            consentDate: new Date(parsedConsent.consentDate)
          });
          setShowConsentBanner(false);
        } else {
          // Policy version changed, need new consent
          console.log('🔄 Cookie policy updated, requesting new consent');
          setShowConsentBanner(true);
        }
      } else {
        // No consent found, show banner
        setShowConsentBanner(true);
      }
    } catch (error) {
      console.error('❌ Error loading cookie consent:', error);
      setShowConsentBanner(true);
    }
  }, []);

  // Save consent to localStorage
  const saveConsent = (newConsent: CookieConsent) => {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(newConsent));
      setConsent(newConsent);
      setShowConsentBanner(false);
      console.log('✅ Cookie consent saved:', newConsent);
    } catch (error) {
      console.error('❌ Error saving cookie consent:', error);
    }
  };

  // Update specific consent categories
  const updateConsent = (updates: Partial<CookieConsent>) => {
    const newConsent: CookieConsent = {
      ...DEFAULT_CONSENT,
      ...consent,
      ...updates,
      necessary: true, // Always true
      consentDate: new Date(),
      version: CURRENT_POLICY_VERSION
    };
    
    saveConsent(newConsent);
  };

  // Check if user has given consent for specific category
  const hasConsent = (category: ConsentCategory): boolean => {
    if (!consent) return category === 'necessary'; // Only necessary cookies before consent
    return consent[category] === true;
  };

  // Check if any consent has been given
  const isConsentGiven = (): boolean => {
    return consent !== null;
  };

  // Accept all cookies
  const acceptAllCookies = () => {
    const allAcceptedConsent: CookieConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
      consentDate: new Date(),
      version: CURRENT_POLICY_VERSION
    };
    
    saveConsent(allAcceptedConsent);
  };

  // Accept only essential cookies
  const acceptEssentialOnly = () => {
    const essentialOnlyConsent: CookieConsent = {
      ...DEFAULT_CONSENT,
      consentDate: new Date(),
      version: CURRENT_POLICY_VERSION
    };
    
    saveConsent(essentialOnlyConsent);
  };

  // Reset consent (for testing or user request)
  const resetConsent = () => {
    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      setConsent(null);
      setShowConsentBanner(true);
      console.log('🔄 Cookie consent reset');
    } catch (error) {
      console.error('❌ Error resetting consent:', error);
    }
  };

  // Get human-readable consent summary
  const getConsentSummary = (): string => {
    if (!consent) return 'No consent given';
    
    const accepted = [];
    if (consent.necessary) accepted.push('Necessary');
    if (consent.analytics) accepted.push('Analytics');
    if (consent.marketing) accepted.push('Marketing');
    if (consent.functional) accepted.push('Functional');
    
    return `Accepted: ${accepted.join(', ')}`;
  };

  // Open cookie settings modal
  const openSettings = () => {
    setShowSettingsModal(true);
  };

  // Close cookie settings modal
  const closeSettings = () => {
    setShowSettingsModal(false);
  };

  const contextValue: CookieConsentContextType = {
    consent,
    updateConsent,
    hasConsent,
    isConsentGiven,
    showConsentBanner,
    showSettingsModal,
    acceptAllCookies,
    acceptEssentialOnly,
    resetConsent,
    getConsentSummary,
    openSettings,
    closeSettings
  };

  return (
    <CookieConsentContext.Provider value={contextValue}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export default CookieConsentContext;